/**
 * アイテム効果フック(docs/02_game_design.md §7.3)。
 *
 * カタログJSON(`catalog/items.json` の `effects`)からそのまま渡せる
 * データ駆動の構造にしてある。エンジンは「効果の合成」だけを知っていればよい。
 */

import { MODIFIER_LIMITS, SCORING } from './config.js';

/** 実装済みのアイテム効果の種類。 */
export type ScoreModifierType =
  /** ゲージ減衰を割合で軽減する(0.25 = -25%)。 */
  | 'gaugeDecayReduction'
  /** コンボ倍率の上限を引き上げる(0.5 = 上限 +0.5)。 */
  | 'comboCapUp'
  /** ミスしてもコンボ・ゲージを維持できる回数(1 = 1回まで)。 */
  | 'missForgive'
  /** コイン獲得量を割合で増やす(0.1 = +10%)。 */
  | 'coinBonus';

/** 1つのアイテム効果。 */
export interface ScoreModifier {
  readonly type: ScoreModifierType;
  readonly value: number;
  /** 由来アイテムID(ログ・デバッグ用。判定には使わない)。 */
  readonly itemId?: string;
}

/** 合成済みの効果値。 */
export interface ResolvedModifiers {
  /** ゲージ減衰の軽減率(0〜MODIFIER_LIMITS.maxGaugeDecayReduction)。 */
  readonly gaugeDecayReduction: number;
  /** コンボ倍率上限(SCORING.comboBonusCap + 引き上げ分)。 */
  readonly comboBonusCap: number;
  /** 1セッションで許されるミス見逃し回数(整数)。 */
  readonly missForgive: number;
  /** コイン獲得倍率(1 + ボーナス)。 */
  readonly coinMultiplier: number;
}

/** 効果なしの既定値。 */
export const NO_MODIFIERS: ResolvedModifiers = {
  gaugeDecayReduction: 0,
  comboBonusCap: SCORING.comboBonusCap,
  missForgive: 0,
  coinMultiplier: 1,
};

/** `resolveModifiers` が非有限値を除去済みなので、範囲クランプだけ行う。 */
function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/**
 * 装備アイテムの効果を合成する。
 * 同種の効果は加算し、それぞれ `MODIFIER_LIMITS` で上限クランプする。
 */
export function resolveModifiers(modifiers: readonly ScoreModifier[] = []): ResolvedModifiers {
  let decay = 0;
  let capUp = 0;
  let forgive = 0;
  let coin = 0;

  for (const modifier of modifiers) {
    const value = Number.isFinite(modifier.value) ? modifier.value : 0;
    switch (modifier.type) {
      case 'gaugeDecayReduction':
        decay += value;
        break;
      case 'comboCapUp':
        capUp += value;
        break;
      case 'missForgive':
        forgive += value;
        break;
      case 'coinBonus':
        coin += value;
        break;
      default:
        break;
    }
  }

  return {
    gaugeDecayReduction: clamp(decay, 0, MODIFIER_LIMITS.maxGaugeDecayReduction),
    comboBonusCap: SCORING.comboBonusCap + clamp(capUp, 0, MODIFIER_LIMITS.maxComboCapUp),
    missForgive: Math.floor(clamp(forgive, 0, MODIFIER_LIMITS.maxMissForgive)),
    coinMultiplier: 1 + clamp(coin, 0, MODIFIER_LIMITS.maxCoinBonus),
  };
}
